import React, { useEffect, useRef, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { mergeCriteria } from './mergeCriteria';
import { canonicalProtocolLink } from 'concepts/Criteria/protocolLink';
import { Toast, ToastButton, createToast, dismissToast } from 'components/core';
import {
  CriteriaAuthError,
  CriteriaConflictError,
  CriteriaResponse,
  CriteriaValidationError,
  updateCriteria,
} from 'query/review/Criteria';
import { getToastConfig } from 'constants/httpErrorToasts';
import { useCriteriaCache } from 'hooks/reviews/useCriteriaCache';
import {
  Criteria,
  CriteriaFramework,
} from 'concepts/Criteria/framework/CriteriaFramework';

// Never reuse an id: react-hot-toast can't dismiss a toast re-created while
// its removal was queued.
let conflictToastSeq = 0;

// The full criteria form shape: the fields component edits the Criteria part;
// only the Settings form registers protocolLink, so it stays optional.
export type CriteriaFormValues = Criteria & {
  protocolLink?: string;
};

export type SaveOutcome = 'saved' | 'failed';

type UseSaveCriteriaOptions = {
  form: UseFormReturn<CriteriaFormValues>;
  framework?: CriteriaFramework;
  version?: string;
  requiresFramework: boolean;
  onComplete?: () => void;
  onRequiresFrameworkError?: (message: string | undefined) => void;
  onConflictFrameworkAdopted?: (framework?: CriteriaFramework) => void;
};

// Save state that must reset once per editing episode: the version baseline
// the next save is stamped with, the conflict toast, and whether the episode
// was dismissed. reset/dismiss let a host whose hook instance outlives a
// single episode (the sidebar) stand in for mount/unmount.
const useSaveReset = (initialVersion?: string) => {
  // Never read from the cache: a background refetch would stamp stale form
  // data with a newer version and skip the 409 (PIER-882). Advances only when
  // the form adopts server data.
  const versionRef = useRef(initialVersion);

  const dismissedRef = useRef(false);

  // Currently shown conflict toast, dismissed on the next successful save.
  const conflictToastIdRef = useRef<string>();

  // Also dismissed when the host unmounts or ends the draft: a toast
  // left behind would outlive the merge it tells the user to review and no
  // later save could dismiss it.
  const unmountedRef = useRef(false);
  const dismissConflictToast = () => {
    if (conflictToastIdRef.current) {
      dismissToast(conflictToastIdRef.current);
      conflictToastIdRef.current = undefined;
    }
  };
  useEffect(
    () => () => {
      unmountedRef.current = true;
      if (conflictToastIdRef.current) dismissToast(conflictToastIdRef.current);
    },
    []
  );

  const reset = (baselineVersion?: string) => {
    versionRef.current = baselineVersion;
    dismissedRef.current = false;
  };

  const dismiss = () => {
    dismissedRef.current = true;
    dismissConflictToast();
  };

  const isAbandoned = () => unmountedRef.current || dismissedRef.current;

  return {
    versionRef,
    conflictToastIdRef,
    dismissConflictToast,
    reset,
    dismiss,
    isAbandoned,
  };
};

type UseSaveCriteriaReturn = {
  saveCriteria: (formData: CriteriaFormValues) => Promise<SaveOutcome>;
  mergeGeneration: number;
  resetSave: (version: string | undefined) => void;
  dismissSave: () => void;
};

export const useSaveCriteria = ({
  form,
  framework,
  version,
  requiresFramework,
  onComplete,
  onRequiresFrameworkError,
  onConflictFrameworkAdopted,
}: UseSaveCriteriaOptions): UseSaveCriteriaReturn => {
  const {
    formState,
    reset,
    getValues,
    getFieldState,
    setError,
    setValue,
  } = form;
  const criteriaCache = useCriteriaCache();

  const {
    versionRef,
    conflictToastIdRef,
    dismissConflictToast,
    reset: resetSave,
    dismiss: dismissSave,
    isAbandoned,
  } = useSaveReset(version);

  // Keys the fieldsets: BulletedTextarea freezes its text at mount, so merges
  // only show after a remount.
  const [mergeGeneration, setMergeGeneration] = useState(0);

  const isProtocolLinkDirty = !!formState.dirtyFields.protocolLink;

  const reportRequiresFrameworkError = (message: string) => {
    if (onRequiresFrameworkError) {
      onRequiresFrameworkError(message);
    } else {
      createToast(<Toast type="danger">{message}</Toast>);
    }
  };

  // The save persisted: adopt the server's state as the form's new baseline.
  const adoptSavedState = async (
    response: CriteriaResponse,
    criteriaValues: Criteria
  ) => {
    versionRef.current = response.version;

    // The response, not the form values: displays render the cache as
    // trusted HTML, which is only safe for server-sanitised content.
    await criteriaCache.setData({
      criteria: response.criteria,
      framework,
      version: response.version,
      protocolLink: response.protocolLink,
    });

    reset({ ...criteriaValues, protocolLink: response.protocolLink ?? '' });
    dismissConflictToast();
    onRequiresFrameworkError?.(undefined);
    createToast(<Toast type="success">Your changes have been saved</Toast>);
    onComplete?.();
  };

  // Nothing was saved; no reset, so Save doubles as the retry.
  const showProtocolLinkError = (error: CriteriaValidationError) => {
    setError('protocolLink', {
      type: 'server',
      message: error.fieldErrors.protocol_link?.[0],
    });
  };

  // Someone else saved first: merge for the user to review — never auto-saved.
  const adoptConflictMerge = async (error: CriteriaConflictError) => {
    // Full snapshot: bumping only the version would let stale criteria pass
    // the digest check.
    await criteriaCache.setData({
      criteria: error.criteria,
      framework: error.framework,
      version: error.version,
      protocolLink: error.protocolLink,
    });

    // Late settle after unmount or draft end (e.g. the panel's X
    // mid-flight): the cache update above still helps, but a toast created
    // now could never be dismissed.
    if (isAbandoned()) return;

    // mergeCriteria treats every key as a category, so the link is split out.
    const { protocolLink: localProtocolLink, ...localValues } = getValues();
    const merged = mergeCriteria(error.criteria, localValues as Criteria);

    // Local edit re-applied on top so a re-typed link stays dirty for the
    // re-save.
    reset(
      {
        ...merged,
        protocolLink: error.protocolLink ?? '',
      } as CriteriaFormValues,
      { keepDirty: true }
    );
    if (getFieldState('protocolLink').isDirty) {
      setValue('protocolLink', localProtocolLink, {
        shouldDirty: true,
      });
    }
    // Remount the frozen BulletedTextarea fields so merged values show.
    setMergeGeneration((generation) => generation + 1);

    // The form now holds the server's data, so adopt its version.
    versionRef.current = error.version;

    // So the re-save can't silently revert the other session's framework
    // change.
    onConflictFrameworkAdopted?.(error.framework);

    // Replace any previous conflict toast instead of stacking.
    if (conflictToastIdRef.current) {
      dismissToast(conflictToastIdRef.current);
    }
    conflictToastIdRef.current = `criteria-conflict-${++conflictToastSeq}`;
    createToast(
      <Toast type="warning">
        Changes were made to the criteria while you were editing. We&apos;ve
        merged both changes. Please review everything before saving.
      </Toast>,
      { id: conflictToastIdRef.current }
    );
  };

  // Anything short of a persisted save — validation error, conflict merge,
  // network failure — is 'failed': the caller stays in edit mode.
  const saveCriteria = async (
    formData: CriteriaFormValues
  ): Promise<SaveOutcome> => {
    if (requiresFramework && !framework) {
      reportRequiresFrameworkError('Please select a question framework');
      return 'failed';
    }

    const { protocolLink: protocolLinkInput, ...criteriaValues } = formData;
    const protocolLinkToSave = isProtocolLinkDirty
      ? canonicalProtocolLink(protocolLinkInput)
      : undefined;

    try {
      // Send the version this form's data is based on (see versionRef).
      const response = await updateCriteria(criteriaValues as Criteria, {
        framework,
        protocolLink: protocolLinkToSave,
        version: versionRef.current,
      });
      await adoptSavedState(response, criteriaValues);
      return 'saved';
    } catch (error) {
      if (
        error instanceof CriteriaValidationError &&
        error.fieldErrors.protocol_link
      ) {
        showProtocolLinkError(error);
        return 'failed';
      }

      if (error instanceof CriteriaConflictError) {
        await adoptConflictMerge(error);
        return 'failed';
      }

      // Session expired mid-save. Keep the draft so Save doubles as the retry
      // once the user has signed in again.
      if (error instanceof CriteriaAuthError) {
        const { message, buttonLabel, buttonLink } = getToastConfig('401');
        createToast(
          <Toast type="danger">
            {message}
            <ToastButton to={buttonLink}>{buttonLabel}</ToastButton>
          </Toast>
        );
        return 'failed';
      }

      createToast(
        <Toast type="danger">
          Unable to save criteria, please try again later
        </Toast>
      );
      return 'failed';
    }
  };

  return {
    saveCriteria,
    mergeGeneration,
    resetSave,
    dismissSave,
  };
};
